import { z } from "zod";

// Checklist Status Enum
export const ChecklistStatusSchema = z.enum(["active", "draft", "inactive"]);

// Checklist Type - now uses typeId reference
// The old enum is deprecated, use ChecklistTypeDTO from ./contracts/type instead

// Checklist Schedule Type Enum
export const ChecklistScheduleTypeSchema = z.enum([
  "daily",
  "opening",
  "closing",
  "scheduled",
]);

// ใช้เมื่อ scheduleType = 'scheduled'
export const ScheduleFrequencySchema = z.enum(["daily", "weekly", "monthly"]);
export const ScheduleDayOfWeekSchema = z.number().int().min(1).max(7); // 1=จันทร์ .. 7=อาทิตย์
export const ScheduleDayOfMonthSchema = z.number().int().min(1).max(31);

// Checklist DTO Schema (for list/get responses)
export const ChecklistSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  typeId: z.string(),
  typeName: z.string().nullable().optional(),
  departmentId: z.string().nullable(),
  departmentName: z.string().nullable().optional(),
  status: ChecklistStatusSchema,
  scheduleType: ChecklistScheduleTypeSchema,
  scheduleFrequency: ScheduleFrequencySchema.nullable().optional(),
  scheduleDayOfWeek: z.number().int().min(1).max(7).nullable().optional(),
  scheduleDayOfMonth: z.number().int().min(1).max(31).nullable().optional(),
  requireAllItems: z.boolean(),
  requirePhoto: z.boolean(),
  requireNotesOnFail: z.boolean(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ChecklistDTO = z.infer<typeof ChecklistSchema>;

// Base Schemas
export const CreateChecklistSchema = z.object({
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ Checklist"),
  description: z.string().optional().nullable(),
  typeId: z.string().min(1, "ກະລຸນາເລືອກປະເພດ"),
  departmentId: z.string().optional().nullable(),
  status: ChecklistStatusSchema.default("draft"),
  scheduleType: ChecklistScheduleTypeSchema.default("daily"),
  scheduleFrequency: ScheduleFrequencySchema.optional().nullable(),
  scheduleDayOfWeek: ScheduleDayOfWeekSchema.optional().nullable(),
  scheduleDayOfMonth: ScheduleDayOfMonthSchema.optional().nullable(),
  requireAllItems: z.boolean().default(false),
  requirePhoto: z.boolean().default(false),
  requireNotesOnFail: z.boolean().default(false),
});

export const UpdateChecklistSchema = CreateChecklistSchema.partial();

// Checklist Item Form Schema (for form arrays)
export const ChecklistItemFormSchema = z.object({
  id: z.string().optional(), // For existing items during edit
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ລາຍການ"),
  description: z.string().optional().nullable(),
  order: z.number().int().min(0),
  isRequired: z.boolean().default(true),
  allowNotes: z.boolean().default(false),
  formId: z.string().nullable().optional(), // ฟอร์มจาก Form Center ที่ผูกกับข้อนี้
});

// JSON schemas for API requests
export const CreateChecklistFormSchema = CreateChecklistSchema.extend({
  items: z.array(ChecklistItemFormSchema).optional(),
});
export type CreateChecklistFormDTO = z.infer<typeof CreateChecklistFormSchema>;

export const UpdateChecklistFormSchema = UpdateChecklistSchema.extend({
  items: z.array(ChecklistItemFormSchema).optional(),
});
export type UpdateChecklistFormDTO = z.infer<typeof UpdateChecklistFormSchema>;

export const CreateChecklistItemSchema = z.object({
  checklistId: z.string().min(1),
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ລາຍການ"),
  description: z.string().optional().nullable(),
  order: z.number().int().min(0),
  isRequired: z.boolean().default(true),
  allowNotes: z.boolean().default(false),
});

export const UpdateChecklistItemSchema =
  CreateChecklistItemSchema.partial().omit({
    checklistId: true,
  });

export const CreateChecklistMappingSchema = z.object({
  checklistId: z.string().min(1),
  sopId: z.string().optional().nullable(),
  flowId: z.string().optional().nullable(),
  positionId: z.string().optional().nullable(),
  departmentId: z.string().optional().nullable(),
});

export const UpdateChecklistMappingSchema =
  CreateChecklistMappingSchema.partial().omit({
    checklistId: true,
  });

// Query Schemas
export const ChecklistQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(10),
  search: z.string().optional(),
  departmentId: z.string().optional(),
  typeId: z.string().optional(),
  status: ChecklistStatusSchema.optional(),
  scheduleType: ChecklistScheduleTypeSchema.optional(),
});

// ID Parameters
export const IdParamSchema = z.object({ id: z.string().min(1) });

// Type exports
export type CreateChecklistDTO = z.infer<typeof CreateChecklistSchema>;
export type UpdateChecklistDTO = z.infer<typeof UpdateChecklistSchema>;
export type ChecklistItemFormDTO = z.infer<typeof ChecklistItemFormSchema>;
export type CreateChecklistItemDTO = z.infer<typeof CreateChecklistItemSchema>;
export type UpdateChecklistItemDTO = z.infer<typeof UpdateChecklistItemSchema>;
export type CreateChecklistMappingDTO = z.infer<
  typeof CreateChecklistMappingSchema
>;
export type UpdateChecklistMappingDTO = z.infer<
  typeof UpdateChecklistMappingSchema
>;
export type ChecklistQueryDTO = z.infer<typeof ChecklistQuerySchema>;
export type IdParamDTO = z.infer<typeof IdParamSchema>;
